// frontend/src/features/workflows/components/WorkflowsOverview.jsx

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PipelineTiles } from './PipelineTiles';
import { AllRunsTable } from './AllRunsTable';
import { Popover } from './Popover';
import { RunPanel } from './RunPanel';
import { SelectExistingSampleModal } from './SelectExistingSampleModal';
import { WORKFLOW_BY_ID } from '../config/pipelines';
import { getRecentRuns } from '../services/batchService';
import { getBatchRunWorkspacePath } from '../utils/runLinks';

export function WorkflowsOverview() {
  const [runs, setRuns] = useState([]);
  const [activePipeline, setActive] = useState(null);
  const [entryPipeline, setEntryPipeline] = useState(null);
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [sampleModalPipeline, setSampleModalPipeline] = useState(null);
  const [isSelectSampleModalOpen, setIsSelectSampleModalOpen] = useState(false);
  const [showRecentRuns, setShowRecentRuns] = useState(false);
  const navigate = useNavigate();

  const loadRuns = useCallback(() => {
    getRecentRuns()
      .then((data) =>
        (data || []).map((run) => ({
          ...run,
          workflowId: run.pipeline,
          workflowTitle: WORKFLOW_BY_ID[run.pipeline]?.title || run.pipeline,
          label: run.sample?.name || run.sample?.id || `Run ${run.id}`,
          startedAt: run.started_at || run.startedAt || run.created_at || null,
        }))
      )
      .then(setRuns);
  }, []);

  const handleShowRecentRuns = () => {
    setShowRecentRuns(true);
    loadRuns();
  };

  // When you start a new run, refetch only if the recent-runs panel is open.
  const onRunClose = () => {
    setActive(null);
    if (showRecentRuns) {
      loadRuns();
    }
  };

  const openEntryModal = (pipelineId) => {
    setEntryPipeline(pipelineId);
    setIsEntryModalOpen(true);
  };

  const handleNewBatch = (pipelineId) => {
    navigate(`/workflow/batches?pipeline=${pipelineId}&mode=batch-upload`);
    setIsEntryModalOpen(false);
  };

  const handleUseExistingSample = (pipelineId) => {
    setIsEntryModalOpen(false);
    setSampleModalPipeline(pipelineId);
    setIsSelectSampleModalOpen(true);
  };

  const handleViewRun = (run) => {
    const workspacePath = getBatchRunWorkspacePath(run);
    if (!workspacePath) {
      navigate('/workflows');
      return;
    }
    navigate(workspacePath);
  };

  return (
    <div className="p-6 space-y-8">
      {/* Workflow tiles */}
      <PipelineTiles
        onRunPipeline={(pipelineId) => {
          if (WORKFLOW_BY_ID[pipelineId]?.action?.type === 'modal') {
            setActive(pipelineId);
          }
        }}
        onOpenBatchEntry={openEntryModal}
      />

      {/* Popover modal */}
      {activePipeline && (
        <Popover onClose={() => setActive(null)}>
          <RunPanel
            pipeline={activePipeline}
            onClose={onRunClose}
          />
        </Popover>
      )}

      {showRecentRuns ? (
        <AllRunsTable
          runs={runs}
          onView={handleViewRun}
          onCancel={() => {
            // TODO: integrate cancellation when backend exposes endpoint
          }}
          onRetry={() => {
            // TODO: integrate retry when backend exposes endpoint
          }}
        />
      ) : (
        <div className="bg-panel rounded-2xl shadow-soft border border-secondary/20 px-4 py-5">
          <button
            type="button"
            onClick={handleShowRecentRuns}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-soft hover:bg-primary/90"
          >
            Show recent runs
          </button>
        </div>
      )}

      {isEntryModalOpen && (
        <Popover onClose={() => setIsEntryModalOpen(false)}>
          <BatchEntryModal
            pipeline={entryPipeline}
            onClose={() => setIsEntryModalOpen(false)}
            onUploadFiles={() => handleNewBatch(entryPipeline)}
            onUseExistingSample={() => handleUseExistingSample(entryPipeline)}
          />
        </Popover>
      )}

      <SelectExistingSampleModal
        open={isSelectSampleModalOpen}
        pipeline={sampleModalPipeline}
        onClose={() => {
          setIsSelectSampleModalOpen(false);
          setSampleModalPipeline(null);
        }}
        onSelect={({ batchId, sampleId, pipeline }) => {
          setIsSelectSampleModalOpen(false);
          setSampleModalPipeline(null);
          navigate(`/workflow/batches?batchId=${batchId}&sampleId=${sampleId}&pipeline=${pipeline}`);
        }}
      />
    </div>
  );
}

function BatchEntryModal({ pipeline, onClose, onUploadFiles, onUseExistingSample }) {
  return (
    <div className="w-full max-w-xl space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-slate-900">
          Start from batch
        </h3>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-700"
        >
          ✕
        </button>
      </div>
      <p className="text-sm text-slate-600">
        Upload files to create a new batch or pick an existing sample to jump into Batch Workspace.
      </p>
      <div className="grid gap-3">
        <button
          onClick={onUploadFiles}
          className="rounded-lg border border-slate-200 bg-slate-900 px-4 py-3 text-left text-white hover:bg-slate-800"
        >
          <span className="block text-sm font-semibold">Upload files (new batch)</span>
          <span className="text-xs text-slate-200">
            Create a batch and add files for {pipeline || 'this pipeline'}.
          </span>
        </button>
        <button
          onClick={onUseExistingSample}
          className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-slate-900 hover:border-slate-300"
        >
          <span className="block text-sm font-semibold">Use existing sample</span>
          <span className="text-xs text-slate-600">
            Search ready samples grouped by batch, then deep-link into Batch Workspace.
          </span>
        </button>
      </div>
    </div>
  );
}
